/**
 * 감사 기록(EA-23) — 계약 XM-71(page).
 *
 * 하루를 `from`·`to`(그날 00:00 ~ 다음 날 00:00, to 미포함)로 부르고 쪽을 나눈다(제안 03 4번).
 * 하루는 관리자 컴퓨터 시간의 하루다 — 표의 시각도 그 시간대로 보이므로 둘이 맞는다(사장님
 * 2026-10-01 — 관리자 웹은 컴퓨터 시간, 03 4번 ④ 바뀜). 날짜 칸은 「yyyy-mm-dd」 글 칸(DateInput).
 * 레거시의 '일부만 표시'는 쪽 나누기로 바뀌었다.
 *
 * ⚠ **범위를 숨기지 않는다.** 권한 이름마다 보이는 기록이 다르다(`auditScope`). 그 사실을
 * 안 알려 주면 「그날은 조용했나 보다」로 읽힌다 — 서버가 건 `scope` 를 그대로 보인다.
 *
 * ip·before·after 는 그리지 않는다(레거시 그대로).
 */
import { useQuery } from '@tanstack/react-query'
import { listAuditLogs } from '@/api/admin'
import { Card, Code, Maybe, TD, TH, Time } from '@/components/ui'
import { QueryState } from '@/components/QueryState'
import { PagePager } from '@/components/Pager'
import { DateInput, FilterBar, useFilters } from '@/components/Filters'
import { localDayRange, localToday } from '@/format'
import { AUDIT_SCOPE_LABEL } from '@/api/types'

const SIZE = 50
const FILTERS = ['day'] as const

export function AuditLogsPage() {
  const f = useFilters(FILTERS)
  // 주소의 날이 없거나 읽히지 않으면 오늘(관리자 컴퓨터 시간).
  const asked = f.values['day'] ?? ''
  const day = localDayRange(asked) !== null ? asked : localToday()
  const range = localDayRange(day)
  const params = { from: range?.from, to: range?.to, page: f.page, size: SIZE }
  const q = useQuery({ queryKey: ['audit', params], queryFn: () => listAuditLogs(params) })
  const scope = q.data?.scope

  return (
    <Card title="Audit logs">
      <FilterBar active={f.active} onClear={f.clear}>
        <DateInput label="Date" value={day} onChange={(v) => f.set('day', v)} />
        {scope && (
          <span className="pb-2 text-xs text-slate-500">
            View scope {(AUDIT_SCOPE_LABEL as Record<string, string>)[scope] ?? ''} <Code>{scope}</Code>
          </span>
        )}
      </FilterBar>

      <QueryState isPending={q.isPending} error={q.error} empty={q.data?.items.length === 0}
        emptyText="No records for that day. (What you can see depends on your view scope.)">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Time</th><th className={TH}>Actor</th><th className={TH}>Permission name</th>
            <th className={TH}>Action</th><th className={TH}>Target</th>
          </tr></thead>
          <tbody>
            {(q.data?.items ?? []).map((r) => (
              <tr key={r.id}>
                <td className={TD}><Time ms={r.at} /></td>
                {/* 배경 작업은 관리자 id 를 비운다 — 「시스템」(명세 4-2). */}
                <td className={TD}>{r.adminLoginId ?? (r.adminId === null ? 'System' : `Admin #${r.adminId}`)}</td>
                <td className={TD}><Maybe value={r.adminRole} /></td>
                <td className={TD}><Code>{r.action}</Code></td>
                <td className={TD}>
                  {r.targetMemberLoginId
                    ?? (r.targetType
                      ? `${r.targetType}${r.targetId ? ` #${r.targetId}` : ''}`
                      : <span className="text-slate-400">—</span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>

      <PagePager page={f.page} size={SIZE} total={q.data?.total} onChange={f.setPage} />
    </Card>
  )
}
